import { FechaCR, MontoCRC, MontoUSD } from '@/components/dominio';
import { NuevaSolicitudDialog } from '@/components/solicitudes-contado/NuevaSolicitudDialog';
import { OrdenCompraDetalle } from '@/components/solicitudes-contado/OrdenCompraDetalle';
import { OrdenesDeSolicitud } from '@/components/solicitudes-contado/OrdenesDeSolicitud';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAccionesListaContado, useListasContado } from '@/hooks/useListasContado';
import {
  useAccionesSolicitudContado,
  useSolicitudesContado,
  useUsuarioActual,
} from '@/hooks/useSolicitudesContado';
import {
  diaDeCR,
  diasEntre,
  formatearFechaHoraCR,
  lunesDeSemana,
  textoHace,
} from '@/lib/fechaHora';
import { cn } from '@/lib/utils';
import type { EstadoSolicitudContado, SolicitudContado } from '@adelante/shared';
import {
  CaretDown,
  CaretRight,
  CaretUp,
  Check,
  CircleNotch,
  ListChecks,
  Plus,
  Trash,
  X,
} from '@phosphor-icons/react';
import { Fragment, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

type Rango = 'ESTA_SEMANA' | 'SEMANA_PASADA' | 'TODO';

const RANGOS: { id: Rango; texto: string; ayuda: string }[] = [
  {
    id: 'ESTA_SEMANA',
    texto: 'Esta semana',
    ayuda: 'Lo de esta semana más todo lo pendiente que se arrastra',
  },
  {
    id: 'SEMANA_PASADA',
    texto: 'Semana pasada',
    ayuda: 'Lo solicitado o resuelto la semana pasada',
  },
  { id: 'TODO', texto: 'Todo', ayuda: 'Todo el historial' },
];

type ColumnaOrden =
  | 'fecha'
  | 'dias'
  | 'proveedor'
  | 'documento'
  | 'descripcion'
  | 'solicitado'
  | 'aprobado'
  | 'estado';

type Orden = { col: ColumnaOrden; dir: 'asc' | 'desc' };

function CabeceraOrden({
  col,
  orden,
  onOrdenar,
  derecha,
  title,
  children,
}: {
  col: ColumnaOrden;
  orden: Orden;
  onOrdenar: (col: ColumnaOrden) => void;
  derecha?: boolean;
  title?: string;
  children: React.ReactNode;
}) {
  const activa = orden.col === col;
  return (
    <TableHead
      className={derecha ? 'text-right' : undefined}
      title={title}
      aria-sort={activa ? (orden.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => onOrdenar(col)}
        className={cn(
          'inline-flex items-center gap-1 hover:text-foreground',
          activa && 'text-foreground',
        )}
      >
        {children}
        {activa &&
          (orden.dir === 'asc' ? <CaretUp className="size-3" /> : <CaretDown className="size-3" />)}
      </button>
    </TableHead>
  );
}

type Filtro = 'ACTIVAS' | 'PENDIENTES' | 'APROBADAS' | 'PAGOS' | 'RECHAZADAS' | 'TODAS';

const FILTROS: { id: Filtro; texto: string; estados: EstadoSolicitudContado[] | null }[] = [
  // Lo que todavía requiere atención: lo ya pagado y lo rechazado quedan en su filtro y en "Todas".
  { id: 'ACTIVAS', texto: 'Activas', estados: ['SOLICITADA', 'APROBADA', 'EN_PROPUESTA'] },
  { id: 'PENDIENTES', texto: 'Pendientes', estados: ['SOLICITADA'] },
  { id: 'APROBADAS', texto: 'Aprobadas', estados: ['APROBADA'] },
  { id: 'PAGOS', texto: 'En archivo / pagadas', estados: ['EN_PROPUESTA', 'PAGADA'] },
  { id: 'RECHAZADAS', texto: 'Rechazadas', estados: ['RECHAZADA'] },
  { id: 'TODAS', texto: 'Todas', estados: null },
];

const BADGE_ESTADO: Record<
  EstadoSolicitudContado,
  { texto: string; variante: 'warning' | 'success' | 'destructive' | 'secondary' | 'outline' }
> = {
  SOLICITADA: { texto: 'Pendiente', variante: 'warning' },
  APROBADA: { texto: 'Aprobada', variante: 'success' },
  RECHAZADA: { texto: 'Rechazada', variante: 'destructive' },
  EN_PROPUESTA: { texto: 'En archivo', variante: 'secondary' },
  PAGADA: { texto: 'Pagada', variante: 'outline' },
};

const TEXTO_TIPO = { ORDEN: 'OC', FACTURA: 'Factura', MIXTO: 'OC + fact.', OTRO: 'Otro' } as const;
const esDolares = (s: SolicitudContado) => !!s.currencyCode && s.currencyCode !== 'CRC';
const aNumero = (texto: string) => Number(texto.trim().replace(',', '.'));
const redondear = (n: number) => Math.round(n * 100) / 100;

function Monto({ valor, dolares }: { valor: number; dolares: boolean }) {
  return dolares ? <MontoUSD monto={valor} /> : <MontoCRC monto={valor} />;
}

/** Rechazar una solicitud pendiente, con un motivo opcional para Proveeduría. */
function RechazarDialog({
  solicitud,
  onClose,
}: { solicitud: SolicitudContado | null; onClose: () => void }) {
  const { rechazar } = useAccionesSolicitudContado();
  const [motivo, setMotivo] = useState('');

  return (
    <Dialog
      open={!!solicitud}
      onOpenChange={(v) => {
        if (!v) {
          setMotivo('');
          onClose();
        }
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Rechazar solicitud</DialogTitle>
          <DialogDescription>
            {solicitud?.nombre} — {solicitud?.descripcion}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1">
          <Label htmlFor="motivo-rechazo">Motivo (opcional)</Label>
          <Input
            id="motivo-rechazo"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            maxLength={300}
            placeholder="Para que Proveeduría sepa por qué"
          />
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            disabled={rechazar.isPending}
            onClick={() =>
              solicitud &&
              rechazar.mutate(
                { id: solicitud.idSolicitud, motivo: motivo.trim() || null },
                {
                  onSuccess: () => {
                    toast.success('Solicitud rechazada');
                    setMotivo('');
                    onClose();
                  },
                  onError: (e) =>
                    toast.error(e instanceof Error ? e.message : 'No se pudo rechazar'),
                },
              )
            }
          >
            Rechazar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SolicitudesContadoPantalla() {
  const { data, isLoading } = useSolicitudesContado();
  const { data: yoData } = useUsuarioActual();
  const acciones = useAccionesSolicitudContado();
  const soloSolicitudes = !!yoData?.data?.soloSolicitudes;
  // Proveeduría no ve las listas de pago: ni se consultan ni se ofrecen.
  const { data: listasData } = useListasContado(!soloSolicitudes);
  const accionesLista = useAccionesListaContado();
  const listaAbierta = (listasData?.data ?? []).find((l) => l.estado === 'ABIERTA');
  const [filtro, setFiltro] = useState<Filtro>('ACTIVAS');
  const [rango, setRango] = useState<Rango>('ESTA_SEMANA');
  const [busqueda, setBusqueda] = useState('');
  const [moneda, setMoneda] = useState<'TODAS' | 'CRC' | 'USD'>('TODAS');
  const [orden, setOrden] = useState<{ col: ColumnaOrden; dir: 'asc' | 'desc' }>({
    col: 'fecha',
    dir: 'asc',
  });
  function ordenarPor(col: ColumnaOrden) {
    setOrden((o) => ({ col, dir: o.col === col && o.dir === 'asc' ? 'desc' : 'asc' }));
  }
  const [dialogNueva, setDialogNueva] = useState(false);
  const [rechazando, setRechazando] = useState<SolicitudContado | null>(null);
  const [ordenDetalle, setOrdenDetalle] = useState<string | null>(null);
  // Monto a aprobar que el administrador va escribiendo por solicitud (si no, el solicitado).
  const [montos, setMontos] = useState<Record<number, string>>({});
  const [seleccion, setSeleccion] = useState<Set<number>>(new Set());
  // Filas desplegadas y, por solicitud, las OC marcadas para pagar (si no, todas).
  const [expandidas, setExpandidas] = useState<Set<number>>(new Set());
  const [ocMarcadas, setOcMarcadas] = useState<Record<number, string[]>>({});
  const [aprobandoLote, setAprobandoLote] = useState(false);
  const [detalle, setDetalleEstado] = useState(() => {
    try {
      return localStorage.getItem('solicitudes-contado-detalle') === '1';
    } catch {
      return false;
    }
  });
  function ponerDetalle(v: boolean) {
    setDetalleEstado(v);
    try {
      localStorage.setItem('solicitudes-contado-detalle', v ? '1' : '0');
    } catch {
      /* sin almacenamiento: no pasa nada */
    }
  }

  const yo = yoData?.data;
  const todas = data?.data ?? [];

  // Lo que cae en el rango elegido: por fecha de solicitud o de resolución; lo pendiente se arrastra.
  const delRangoBase = useMemo(() => {
    if (rango === 'TODO') return todas;
    const desde = lunesDeSemana(undefined, rango === 'ESTA_SEMANA' ? 0 : -1);
    const hasta = lunesDeSemana(undefined, rango === 'ESTA_SEMANA' ? 1 : 0); // exclusivo
    const dentro = (d: string) => d >= desde && d < hasta;
    return todas.filter(
      (s) =>
        (rango === 'ESTA_SEMANA' && (s.estado === 'SOLICITADA' || diaDeCR(s.fecha) >= desde)) ||
        dentro(diaDeCR(s.fecha)) ||
        (!!s.resueltoEn && dentro(diaDeCR(s.resueltoEn))),
    );
  }, [todas, rango]);

  // El filtro de moneda se aplica sobre lo del rango: los conteos y los totales lo siguen.
  const delRango = useMemo(
    () =>
      moneda === 'TODAS'
        ? delRangoBase
        : delRangoBase.filter((s) => esDolares(s) === (moneda === 'USD')),
    [delRangoBase, moneda],
  );

  const conteo = useMemo(() => {
    const c = {} as Record<Filtro, number>;
    for (const f of FILTROS)
      c[f.id] = delRango.filter((s) => !f.estados || f.estados.includes(s.estado)).length;
    return c;
  }, [delRango]);

  const visibles = useMemo(() => {
    const f = FILTROS.find((x) => x.id === filtro);
    const texto = busqueda.trim().toLowerCase();
    const lista = delRango.filter(
      (s) =>
        (!f?.estados || f.estados.includes(s.estado)) &&
        (!texto ||
          [s.nombre, s.descripcion, s.documentoBC, s.referencia, s.solicitadoPorEmail]
            .filter(Boolean)
            .some((v) => (v as string).toLowerCase().includes(texto))),
    );
    // Por defecto como el Excel: por fecha de solicitud, lo más viejo arriba.
    const dir = orden.dir === 'asc' ? 1 : -1;
    const valor = (s: SolicitudContado): string | number => {
      switch (orden.col) {
        case 'fecha':
          return s.fecha;
        case 'dias':
          return diasEntre(
            s.fecha,
            s.estado === 'SOLICITADA' ? undefined : (s.resueltoEn ?? undefined),
          );
        case 'proveedor':
          return s.nombre.toLowerCase();
        case 'documento':
          return (s.documentoBC ?? s.referencia ?? '').toLowerCase();
        case 'descripcion':
          return (s.descripcion ?? '').toLowerCase();
        case 'solicitado':
          return s.montoSolicitado;
        case 'aprobado':
          return s.montoAprobado ?? -1;
        case 'estado':
          return s.estado;
      }
    };
    return [...lista].sort((a, b) => {
      const va = valor(a);
      const vb = valor(b);
      const c = typeof va === 'number' ? va - (vb as number) : va.localeCompare(vb as string);
      return c * dir || a.fecha.localeCompare(b.fecha) || a.idSolicitud - b.idSolicitud;
    });
  }, [delRango, filtro, busqueda, orden]);

  // Igual que la fila de totales del Excel: lo solicitado frente a lo aprobado, por moneda.
  const totales = useMemo(() => {
    const t = { crc: { sol: 0, apr: 0, pen: 0 }, usd: { sol: 0, apr: 0, pen: 0 } };
    for (const s of visibles) {
      const m = esDolares(s) ? t.usd : t.crc;
      if (s.estado === 'RECHAZADA') continue;
      m.sol += s.montoSolicitado;
      if (s.estado === 'SOLICITADA') m.pen += s.montoSolicitado;
      m.apr += s.montoAprobado ?? 0;
    }
    return t;
  }, [visibles]);

  // La lista vigente: la abierta o, si no hay, la más reciente.
  const listaVigente = listaAbierta ?? (listasData?.data ?? []).find((l) => !l.pagada);
  const esAdmin = !!yo?.esAdmin;
  const esMia = (s: SolicitudContado) =>
    !!yo && s.solicitadoPorEmail.toLowerCase() === yo.email.toLowerCase();
  const montoDe = (s: SolicitudContado) => montos[s.idSolicitud] ?? String(s.montoSolicitado);
  const montoValido = (s: SolicitudContado) => aNumero(montoDe(s)) > 0;
  const tieneOC = (s: SolicitudContado) =>
    (s.tipoDocumento === 'ORDEN' || s.tipoDocumento === 'FACTURA' || s.tipoDocumento === 'MIXTO') &&
    s.documentos.length > 0;
  const marcadasDe = (s: SolicitudContado) =>
    ocMarcadas[s.idSolicitud] ?? s.documentos.map((d) => d.numero);
  /** OC elegidas solo si hay alguna sin marcar; si están todas, se aprueba la solicitud completa. */
  const documentosParaAprobar = (s: SolicitudContado) =>
    tieneOC(s) && marcadasDe(s).length < s.documentos.length ? marcadasDe(s) : undefined;

  function alternarFila(id: number) {
    setExpandidas((actual) => {
      const nuevo = new Set(actual);
      if (nuevo.has(id)) nuevo.delete(id);
      else nuevo.add(id);
      return nuevo;
    });
  }

  const pendientesVisibles = esAdmin ? visibles.filter((s) => s.estado === 'SOLICITADA') : [];
  const seleccionadas = pendientesVisibles.filter((s) => seleccion.has(s.idSolicitud));
  const todasMarcadas =
    pendientesVisibles.length > 0 && seleccionadas.length === pendientesVisibles.length;

  const totalSeleccion = useMemo(() => {
    const t = { crc: 0, usd: 0 };
    for (const s of seleccionadas) {
      const n = aNumero(montos[s.idSolicitud] ?? String(s.montoSolicitado));
      if (n > 0) {
        if (esDolares(s)) t.usd += n;
        else t.crc += n;
      }
    }
    return t;
  }, [seleccionadas, montos]);

  function ponerMonto(s: SolicitudContado, valor: string) {
    setMontos((m) => ({ ...m, [s.idSolicitud]: valor }));
  }

  function alternar(id: number) {
    setSeleccion((actual) => {
      const nuevo = new Set(actual);
      if (nuevo.has(id)) nuevo.delete(id);
      else nuevo.add(id);
      return nuevo;
    });
  }

  function liberarSaldo(s: SolicitudContado) {
    const saldo = s.montoSolicitado - (s.montoAprobado ?? 0);
    if (
      !confirm(
        `Se aprobó ${s.montoAprobado} de ${s.montoSolicitado}. ¿Dejar la diferencia (${saldo.toLocaleString('es-CR')}) como una solicitud nueva pendiente?`,
      )
    )
      return;
    acciones.liberarSaldo.mutate(s.idSolicitud, {
      onSuccess: () => toast.success(`Saldo de ${s.nombre} pendiente de aprobar`),
      onError: (er) =>
        toast.error(er instanceof Error ? er.message : 'No se pudo liberar el saldo'),
    });
  }

  function aprobarUna(s: SolicitudContado) {
    const monto = aNumero(montoDe(s));
    if (!(monto > 0)) return;
    const documentos = documentosParaAprobar(s);
    if (documentos) {
      const sinMarcar = s.documentos.length - documentos.length;
      if (
        !confirm(
          `Las ${sinMarcar} sin marcar quedarán como una solicitud nueva pendiente. ¿Aprobar?`,
        )
      )
        return;
    }
    acciones.aprobar.mutate(
      { id: s.idSolicitud, monto, documentos },
      {
        onSuccess: (r) => {
          toast.success(
            r.data?.divididaEn
              ? `Aprobada: ${s.nombre}. Lo que no marcó quedó como una solicitud nueva.`
              : r.data?.saldoEn
                ? `Aprobada: ${s.nombre}. La diferencia quedó como una solicitud nueva pendiente.`
                : `Aprobada: ${s.nombre}`,
          );
          setSeleccion((a) => {
            const n = new Set(a);
            n.delete(s.idSolicitud);
            return n;
          });
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo aprobar'),
      },
    );
  }

  async function aprobarLote() {
    const lote = seleccionadas.filter(montoValido);
    if (lote.length === 0) return;
    setAprobandoLote(true);
    const resultados = await Promise.allSettled(
      lote.map((s) =>
        acciones.aprobar.mutateAsync({
          id: s.idSolicitud,
          monto: aNumero(montoDe(s)),
          documentos: documentosParaAprobar(s),
        }),
      ),
    );
    setAprobandoLote(false);
    const fallidas = resultados.filter((r) => r.status === 'rejected').length;
    const hechas = resultados.length - fallidas;
    if (hechas > 0) toast.success(`${hechas} solicitud(es) aprobada(s)`);
    if (fallidas > 0) toast.error(`${fallidas} no se pudieron aprobar`);
    setSeleccion(new Set());
  }

  function borrar(s: SolicitudContado) {
    if (!confirm(`¿Borrar la solicitud de ${s.nombre} (${s.descripcion})?`)) return;
    acciones.eliminar.mutate(s.idSolicitud, {
      onSuccess: () => toast.success('Solicitud borrada'),
      onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo borrar'),
    });
  }

  function reabrir(s: SolicitudContado) {
    acciones.reabrir.mutate(s.idSolicitud, {
      onSuccess: () => toast.success('Solicitud reabierta'),
      onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo reabrir'),
    });
  }

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Solicitudes de contado</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Pagos de contado que pide Proveeduría. El administrador aprueba el monto, que puede ser
            menos de lo solicitado, y solo lo aprobado se paga; lo pendiente se arrastra a la semana
            siguiente.
          </p>
        </div>
        <div className="flex gap-2">
          {listaVigente && (
            <Button asChild variant="outline">
              <Link
                to={`/vencimientos/listas-contado/${listaVigente.idLista}`}
                state={{ desde: 'solicitudes' }}
              >
                <ListChecks className="h-4 w-4" weight="bold" aria-hidden="true" />
                {listaVigente.estado === 'ABIERTA' ? 'Lista vigente' : 'Última lista'}:{' '}
                {listaVigente.nombre}
              </Link>
            </Button>
          )}
          <Button onClick={() => setDialogNueva(true)}>
            <Plus className="h-4 w-4" weight="bold" aria-hidden="true" />
            Nueva solicitud
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <fieldset
          aria-label="Semana"
          className="m-0 inline-flex overflow-hidden rounded-md border border-border p-0"
        >
          {RANGOS.map((x) => (
            <Button
              key={x.id}
              type="button"
              title={x.ayuda}
              variant={rango === x.id ? 'default' : 'ghost'}
              aria-pressed={rango === x.id}
              className="rounded-none"
              onClick={() => setRango(x.id)}
            >
              {x.texto}
            </Button>
          ))}
        </fieldset>
        <fieldset
          aria-label="Estado de las solicitudes"
          className="m-0 inline-flex overflow-hidden rounded-md border border-border p-0"
        >
          {FILTROS.map((f) => (
            <Button
              key={f.id}
              type="button"
              variant={filtro === f.id ? 'default' : 'ghost'}
              aria-pressed={filtro === f.id}
              className="rounded-none"
              onClick={() => setFiltro(f.id)}
            >
              {f.texto}
              <span className="ml-2 text-xs opacity-70">{conteo[f.id]}</span>
            </Button>
          ))}
        </fieldset>
        <fieldset
          aria-label="Moneda de las solicitudes"
          className="m-0 inline-flex overflow-hidden rounded-md border border-border p-0"
        >
          {(
            [
              { id: 'TODAS', texto: 'Ambas monedas', n: delRangoBase.length },
              {
                id: 'CRC',
                texto: '₡ Colones',
                n: delRangoBase.filter((s) => !esDolares(s)).length,
              },
              { id: 'USD', texto: '$ Dólares', n: delRangoBase.filter((s) => esDolares(s)).length },
            ] as const
          ).map((m) => (
            <Button
              key={m.id}
              type="button"
              variant={moneda === m.id ? 'default' : 'ghost'}
              aria-pressed={moneda === m.id}
              className="rounded-none"
              onClick={() => setMoneda(m.id)}
            >
              {m.texto}
              <span className="ml-2 text-xs opacity-70">{m.n}</span>
            </Button>
          ))}
        </fieldset>
        <Input
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="Buscar proveedor, OC, descripción…"
          className="w-72"
        />
        <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
          <input
            type="checkbox"
            checked={detalle}
            onChange={(e) => ponerDetalle(e.target.checked)}
          />
          Mostrar más detalle
        </label>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {(
          [
            ['Colones', totales.crc, false],
            ['Dólares', totales.usd, true],
          ] as const
        ).map(([titulo, t, dolares]) => (
          <Card key={titulo}>
            <CardContent className="flex items-center justify-between gap-4 pt-6">
              <span className="text-sm font-medium">{titulo}</span>
              <div className="flex gap-6 text-sm">
                <div className="text-right">
                  <div className="text-xs text-muted-foreground">Solicitado</div>
                  <Monto valor={t.sol} dolares={dolares} />
                </div>
                <div className="text-right">
                  <div className="text-xs text-muted-foreground">Pendiente</div>
                  <Monto valor={t.pen} dolares={dolares} />
                </div>
                <div className="text-right">
                  <div className="text-xs text-muted-foreground">Aprobado</div>
                  <Monto valor={t.apr} dolares={dolares} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {listaAbierta && (
        <Card className="border-adelante-primary/50 bg-adelante-primary/10">
          <CardContent className="flex flex-wrap items-center gap-x-4 gap-y-2 py-3 text-sm">
            <span>
              Lista abierta: <span className="font-medium">{listaAbierta.nombre}</span>
            </span>
            <span className="text-muted-foreground">
              {listaAbierta.cantidadSolicitudes} aprobada(s)
              {listaAbierta.totales.map((t) => (
                <span key={t.moneda}>
                  {' · '}
                  {t.moneda === 'USD' ? (
                    <MontoUSD monto={t.aprobado} />
                  ) : (
                    <MontoCRC monto={t.aprobado} />
                  )}
                </span>
              ))}
            </span>
            <div className="ml-auto flex gap-2">
              <Button asChild size="sm" variant="outline">
                <Link
                  to={`/vencimientos/listas-contado/${listaAbierta.idLista}`}
                  state={{ desde: 'solicitudes' }}
                >
                  Ver la lista
                </Link>
              </Button>
              {esAdmin && (
                <Button
                  size="sm"
                  disabled={
                    accionesLista.cerrar.isPending || listaAbierta.cantidadSolicitudes === 0
                  }
                  title="Termina la aprobación: Contabilidad la ve lista para pagar"
                  onClick={() => {
                    if (
                      !confirm(
                        `¿Cerrar "${listaAbierta.nombre}" y pasarla a Contabilidad? Lo que apruebe después irá a una lista nueva.`,
                      )
                    )
                      return;
                    accionesLista.cerrar.mutate(listaAbierta.idLista, {
                      onSuccess: () => toast.success('Lista cerrada: lista para pagar'),
                      onError: (e) =>
                        toast.error(e instanceof Error ? e.message : 'No se pudo cerrar'),
                    });
                  }}
                >
                  Cerrar y pasar a Contabilidad
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {esAdmin && seleccionadas.length > 0 && (
        <Card className="border-adelante-primary/50 bg-adelante-primary/10">
          <CardContent className="flex flex-wrap items-center gap-4 py-3 text-sm">
            <span className="font-medium">{seleccionadas.length} seleccionada(s)</span>
            <span className="text-muted-foreground">
              A aprobar: {totalSeleccion.crc > 0 && <MontoCRC monto={totalSeleccion.crc} />}
              {totalSeleccion.crc > 0 && totalSeleccion.usd > 0 && ' · '}
              {totalSeleccion.usd > 0 && <MontoUSD monto={totalSeleccion.usd} />}
            </span>
            <div className="ml-auto flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setSeleccion(new Set())}>
                Limpiar selección
              </Button>
              <Button
                size="sm"
                disabled={aprobandoLote || seleccionadas.some((s) => !montoValido(s))}
                onClick={aprobarLote}
              >
                <Check className="h-3.5 w-3.5" weight="bold" aria-hidden="true" />
                {aprobandoLote ? 'Aprobando…' : 'Aprobar con los montos indicados'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <CircleNotch className="h-4 w-4 animate-spin" weight="bold" aria-hidden="true" />
          Cargando…
        </div>
      ) : visibles.length === 0 ? (
        <Card>
          <CardContent className="pt-6 text-sm text-muted-foreground">
            {todas.length === 0
              ? 'Todavía no hay solicitudes. Registrá la primera con "Nueva solicitud".'
              : 'No hay solicitudes en esta vista. Probá con otro rango o filtro.'}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="pt-6">
            <Table>
              <TableHeader>
                <TableRow>
                  {esAdmin && pendientesVisibles.length > 0 && (
                    <TableHead className="w-8">
                      <input
                        type="checkbox"
                        aria-label="Seleccionar todas las pendientes"
                        checked={todasMarcadas}
                        onChange={() =>
                          setSeleccion(
                            todasMarcadas
                              ? new Set()
                              : new Set(pendientesVisibles.map((s) => s.idSolicitud)),
                          )
                        }
                      />
                    </TableHead>
                  )}
                  <CabeceraOrden col="fecha" orden={orden} onOrdenar={ordenarPor}>
                    Solicitada
                  </CabeceraOrden>
                  <CabeceraOrden
                    col="dias"
                    orden={orden}
                    onOrdenar={ordenarPor}
                    derecha
                    title="Pendiente: días esperando. Resuelta: días desde que se solicitó hasta que se aprobó o rechazó"
                  >
                    Días
                  </CabeceraOrden>
                  <CabeceraOrden col="proveedor" orden={orden} onOrdenar={ordenarPor}>
                    Proveedor
                  </CabeceraOrden>
                  <CabeceraOrden col="documento" orden={orden} onOrdenar={ordenarPor}>
                    Documento
                  </CabeceraOrden>
                  <CabeceraOrden col="descripcion" orden={orden} onOrdenar={ordenarPor}>
                    Descripción
                  </CabeceraOrden>
                  <CabeceraOrden col="solicitado" orden={orden} onOrdenar={ordenarPor} derecha>
                    Solicitado
                  </CabeceraOrden>
                  <CabeceraOrden col="aprobado" orden={orden} onOrdenar={ordenarPor} derecha>
                    Aprobado
                  </CabeceraOrden>
                  <CabeceraOrden col="estado" orden={orden} onOrdenar={ordenarPor}>
                    Estado
                  </CabeceraOrden>
                  <TableHead className="w-44" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibles.map((s) => {
                  const b = BADGE_ESTADO[s.estado];
                  const pendiente = s.estado === 'SOLICITADA';
                  const espera = pendiente ? diasEntre(s.fecha) : 0;
                  const editable = pendiente && esAdmin;
                  const monto = aNumero(montoDe(s));
                  const dolares = esDolares(s);
                  const porcentaje =
                    s.montoAprobado !== null
                      ? Math.round((s.montoAprobado / s.montoSolicitado) * 100)
                      : null;
                  return (
                    <Fragment key={s.idSolicitud}>
                      <TableRow
                        className={cn(
                          s.estado === 'APROBADA' && 'bg-green-50 hover:bg-green-50',
                          (s.estado === 'EN_PROPUESTA' || s.estado === 'PAGADA') &&
                            'bg-emerald-100/70 hover:bg-emerald-100/70',
                          s.estado === 'RECHAZADA' &&
                            'bg-red-50 text-muted-foreground hover:bg-red-50',
                        )}
                      >
                        {esAdmin && pendientesVisibles.length > 0 && (
                          <TableCell>
                            {editable && (
                              <input
                                type="checkbox"
                                aria-label={`Seleccionar ${s.nombre}`}
                                checked={seleccion.has(s.idSolicitud)}
                                onChange={() => alternar(s.idSolicitud)}
                              />
                            )}
                          </TableCell>
                        )}
                        <TableCell className="whitespace-nowrap">
                          <div className="flex items-center gap-1">
                            {tieneOC(s) ? (
                              <button
                                type="button"
                                aria-expanded={expandidas.has(s.idSolicitud)}
                                title="Ver el detalle de la solicitud"
                                onClick={() => alternarFila(s.idSolicitud)}
                                className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                              >
                                {expandidas.has(s.idSolicitud) ? (
                                  <CaretDown className="h-4 w-4" weight="bold" aria-hidden="true" />
                                ) : (
                                  <CaretRight
                                    className="h-4 w-4"
                                    weight="bold"
                                    aria-hidden="true"
                                  />
                                )}
                              </button>
                            ) : (
                              <span className="inline-block w-5" />
                            )}
                            <div
                              title={`Registrada en el sistema el ${formatearFechaHoraCR(s.solicitadoEn)} por ${s.solicitadoPorEmail}`}
                            >
                              <FechaCR fecha={s.fecha} />
                              {detalle && (
                                <div className="text-[11px] text-muted-foreground">
                                  registrada {formatearFechaHoraCR(s.solicitadoEn)}
                                </div>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell
                          className={cn(
                            'text-right tabular-nums',
                            pendiente && espera >= 7
                              ? 'font-semibold text-red-700'
                              : pendiente && espera >= 3
                                ? 'font-semibold text-amber-700'
                                : 'text-muted-foreground',
                          )}
                          title={
                            pendiente
                              ? `Esperando ${textoHace(espera)}`
                              : s.resueltoEn
                                ? `Tardó ${diasEntre(s.fecha, s.resueltoEn)} día(s) en resolverse`
                                : undefined
                          }
                        >
                          {pendiente
                            ? espera
                            : s.resueltoEn
                              ? diasEntre(s.fecha, s.resueltoEn)
                              : '—'}
                        </TableCell>
                        <TableCell className="font-medium">
                          {s.nombre}
                          {detalle && (
                            <div className="text-xs font-normal text-muted-foreground">
                              {s.solicitadoPorEmail}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="min-w-[8rem]">
                          {(detalle || s.tipoDocumento === 'OTRO') && (
                            <Badge variant="outline" className="mr-1 font-normal">
                              {TEXTO_TIPO[s.tipoDocumento]}
                            </Badge>
                          )}
                          {s.tipoDocumento === 'FACTURA' ? (
                            <span
                              className={cn(
                                'font-medium',
                                !detalle && 'inline-block max-w-[11rem] truncate align-middle',
                              )}
                              title={s.documentoBC ?? undefined}
                            >
                              {s.documentoBC}
                            </span>
                          ) : (s.tipoDocumento === 'ORDEN' || s.tipoDocumento === 'MIXTO') &&
                            s.documentos.length > 0 ? (
                            <span className="inline-flex flex-wrap gap-x-2 gap-y-0.5 align-middle">
                              {(detalle ? s.documentos : s.documentos.slice(0, 2)).map((d) =>
                                d.entryNo ? (
                                  <span key={d.numero} className="font-medium" title="Factura">
                                    {d.numero}
                                  </span>
                                ) : (
                                  <button
                                    key={d.numero}
                                    type="button"
                                    title="Ver la orden: obra y líneas"
                                    onClick={() => setOrdenDetalle(d.numero)}
                                    className="font-medium underline decoration-dotted underline-offset-2 hover:text-adelante-primary-dark"
                                  >
                                    {d.numero}
                                  </button>
                                ),
                              )}
                              {!detalle && s.documentos.length > 2 && (
                                <button
                                  type="button"
                                  title={s.documentos
                                    .slice(2)
                                    .map((d) => d.numero)
                                    .join(', ')}
                                  onClick={() => alternarFila(s.idSolicitud)}
                                  className="text-xs text-muted-foreground hover:text-foreground"
                                >
                                  +{s.documentos.length - 2} más
                                </button>
                              )}
                            </span>
                          ) : (
                            (s.documentoBC ?? '')
                          )}
                          {detalle && s.referencia && (
                            <div className="text-xs text-muted-foreground">{s.referencia}</div>
                          )}
                        </TableCell>
                        <TableCell className="min-w-[10rem] max-w-xs">{s.descripcion}</TableCell>
                        <TableCell className="text-right">
                          <Monto valor={s.montoSolicitado} dolares={dolares} />
                        </TableCell>
                        <TableCell className="text-right">
                          {editable ? (
                            <div className="flex flex-col items-end gap-1">
                              <Input
                                aria-label={`Monto a aprobar de ${s.nombre}`}
                                inputMode="decimal"
                                value={montoDe(s)}
                                onChange={(e) => ponerMonto(s, e.target.value)}
                                className={cn(
                                  'h-8 w-32 text-right tabular-nums',
                                  monto > 0 && monto < s.montoSolicitado && 'border-amber-400',
                                  (!(monto > 0) || monto > s.montoSolicitado) && 'border-red-400',
                                )}
                              />
                              <div className="flex gap-0.5">
                                {[100, 75, 50, 33, 25].map((p) => (
                                  <button
                                    key={p}
                                    type="button"
                                    title={`Aprobar el ${p}% de lo solicitado`}
                                    onClick={() =>
                                      ponerMonto(
                                        s,
                                        // "33" es un tercio exacto, como en la hoja semanal.
                                        String(
                                          redondear(
                                            (s.montoSolicitado * (p === 33 ? 100 / 3 : p)) / 100,
                                          ),
                                        ),
                                      )
                                    }
                                    className="rounded border px-1 py-0.5 text-[11px] tabular-nums text-muted-foreground hover:bg-muted hover:text-foreground"
                                  >
                                    {p}
                                  </button>
                                ))}
                              </div>
                              {monto > s.montoSolicitado && (
                                <span className="text-[11px] text-red-600">
                                  Mayor a lo solicitado
                                </span>
                              )}
                            </div>
                          ) : s.montoAprobado !== null ? (
                            <div
                              className={cn(
                                s.montoAprobado < s.montoSolicitado && 'font-medium text-amber-700',
                              )}
                            >
                              <Monto valor={s.montoAprobado} dolares={dolares} />
                              {porcentaje !== null && porcentaje !== 100 && (
                                <div className="text-[11px] font-normal">{porcentaje}%</div>
                              )}
                            </div>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={b.variante}
                            title={
                              [
                                s.nombreLista,
                                s.resueltoEn &&
                                  `${s.estado === 'RECHAZADA' ? 'Rechazada' : 'Aprobada'} ${formatearFechaHoraCR(s.resueltoEn)} por ${s.resueltoPorEmail}`,
                                s.estado === 'RECHAZADA' && s.motivoRechazo,
                              ]
                                .filter(Boolean)
                                .join(' · ') || undefined
                            }
                          >
                            {b.texto}
                          </Badge>
                          {detalle && s.idLista && (
                            <div className="mt-1 text-xs">
                              <Link
                                to={`/vencimientos/listas-contado/${s.idLista}`}
                                state={{ desde: 'solicitudes' }}
                                className="text-muted-foreground underline decoration-dotted underline-offset-2 hover:text-foreground"
                              >
                                {s.nombreLista}
                              </Link>
                            </div>
                          )}
                          {detalle && s.resueltoEn && (
                            <div
                              className="mt-1 text-[11px] text-muted-foreground"
                              title={`${s.estado === 'RECHAZADA' ? 'Rechazada' : 'Aprobada'} por ${s.resueltoPorEmail}`}
                            >
                              {s.estado === 'RECHAZADA' ? 'Rechazada' : 'Aprobada'}{' '}
                              {formatearFechaHoraCR(s.resueltoEn)}
                            </div>
                          )}
                          {detalle && s.estado === 'RECHAZADA' && s.motivoRechazo && (
                            <div className="mt-1 text-xs text-muted-foreground">
                              {s.motivoRechazo}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            {esAdmin &&
                              s.montoAprobado !== null &&
                              s.montoAprobado < s.montoSolicitado - 0.005 &&
                              !s.saldoLiberado &&
                              ['APROBADA', 'EN_PROPUESTA', 'PAGADA'].includes(s.estado) && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  title="Deja lo que no se aprobó como una solicitud nueva pendiente de aprobar"
                                  disabled={acciones.liberarSaldo.isPending}
                                  onClick={() => liberarSaldo(s)}
                                >
                                  Liberar saldo
                                </Button>
                              )}
                            {editable && (
                              <>
                                <Button
                                  size="sm"
                                  title="Aprobar con el monto indicado"
                                  disabled={!montoValido(s) || acciones.aprobar.isPending}
                                  onClick={() => aprobarUna(s)}
                                >
                                  <Check className="h-3.5 w-3.5" weight="bold" aria-hidden="true" />
                                  Aprobar
                                </Button>
                                <Button
                                  size="icon"
                                  variant="outline"
                                  className="h-8 w-8"
                                  title="Rechazar"
                                  onClick={() => setRechazando(s)}
                                >
                                  <X className="h-3.5 w-3.5" weight="bold" aria-hidden="true" />
                                </Button>
                              </>
                            )}
                            {esAdmin &&
                              (s.estado === 'APROBADA' || s.estado === 'RECHAZADA') &&
                              !s.idPropuesta && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  title="Devolverla a pendiente para cambiar el monto"
                                  onClick={() => reabrir(s)}
                                >
                                  Reabrir
                                </Button>
                              )}
                            {pendiente && (esMia(s) || !!yo?.puedePagar) && (
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8 text-muted-foreground"
                                title="Borrar"
                                onClick={() => borrar(s)}
                              >
                                <Trash className="h-3.5 w-3.5" weight="bold" aria-hidden="true" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                      {tieneOC(s) && expandidas.has(s.idSolicitud) && (
                        <TableRow className="bg-muted/20 hover:bg-muted/20">
                          <TableCell colSpan={esAdmin && pendientesVisibles.length > 0 ? 10 : 9}>
                            <OrdenesDeSolicitud
                              solicitud={s}
                              editable={editable}
                              seleccion={marcadasDe(s)}
                              onCambio={(lista, suma) => {
                                setOcMarcadas((m) => ({ ...m, [s.idSolicitud]: lista }));
                                if (suma !== null && lista.length > 0)
                                  ponerMonto(s, String(redondear(suma)));
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <NuevaSolicitudDialog open={dialogNueva} onClose={() => setDialogNueva(false)} />
      <RechazarDialog solicitud={rechazando} onClose={() => setRechazando(null)} />
      <Dialog open={!!ordenDetalle} onOpenChange={(v) => !v && setOrdenDetalle(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Orden {ordenDetalle}</DialogTitle>
            <DialogDescription>Datos de la orden de compra en Business Central.</DialogDescription>
          </DialogHeader>
          {ordenDetalle && <OrdenCompraDetalle numero={ordenDetalle} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
